/**
 * Skill Management Service
 * Handles all skill-related operations including skill tracking, assessments,
 * progress monitoring, and skill development recommendations
 */

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    multiGet: async (keys) => keys.map(key => [key, null]),
    removeItem: async (key) => {}
  };
}

class SkillManagementService {
  constructor() {
    this.isInitialized = false;
    this.skillsData = {
      userSkills: [],
      skillAssessments: [],
      skillProgress: {},
      skillRecommendations: [],
      skillCategories: []
    };
    this.subscribers = new Map();
    this.offlineQueue = [];
  }

  // Initialize the service
  async initialize() {
    try {
      console.log('🚀 Initializing Skill Management Service...');
      
      // Load cached skill data
      await this.loadCachedData();
      
      // Initialize skill categories
      await this.initializeSkillCategories();
      
      // Load skill recommendations
      await this.loadSkillRecommendations();
      
      this.isInitialized = true;
      console.log('✅ Skill Management Service initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Skill Management Service:', error);
      return false;
    }
  }

  // Load cached skill data
  async loadCachedData() {
    try {
      const keys = [
        'user_skills',
        'skill_assessments',
        'skill_progress',
        'skill_recommendations'
      ];
      
      const cachedData = await AsyncStorage.multiGet(keys);
      
      cachedData.forEach(([key, value]) => {
        if (value) {
          const dataKey = key.replace('_', '');
          this.skillsData[dataKey] = JSON.parse(value);
        }
      });
      
      console.log('📦 Skill cached data loaded');
    } catch (error) {
      console.error('❌ Error loading cached skill data:', error);
    }
  }

  // Initialize skill categories
  async initializeSkillCategories() {
    try {
      const categories = [
        {
          id: 'technical',
          name: 'Technical Skills',
          description: 'Programming, software, and technical competencies',
          icon: 'code',
          color: '#2196F3'
        },
        {
          id: 'soft',
          name: 'Soft Skills',
          description: 'Communication, leadership, and interpersonal skills',
          icon: 'people',
          color: '#4CAF50'
        },
        {
          id: 'business',
          name: 'Business Skills',
          description: 'Management, strategy, and business acumen',
          icon: 'business',
          color: '#FF9800'
        },
        {
          id: 'creative',
          name: 'Creative Skills',
          description: 'Design, writing, and creative abilities',
          icon: 'palette',
          color: '#9C27B0'
        },
        {
          id: 'analytical',
          name: 'Analytical Skills',
          description: 'Data analysis, research, and problem-solving',
          icon: 'analytics',
          color: '#F44336'
        }
      ];
      
      this.skillsData.skillCategories = categories;
      await AsyncStorage.setItem('skill_categories', JSON.stringify(categories));
      
    } catch (error) {
      console.error('❌ Error initializing skill categories:', error);
    }
  }

  // Get user skills
  async getUserSkills(userId) {
    try {
      console.log('🎯 Getting user skills for:', userId);
      
      // Return cached skills if available
      if (this.skillsData.userSkills.length > 0) {
        return {
          success: true,
          data: this.skillsData.userSkills
        };
      }
      
      // In a real app, this would fetch from API
      // For now, return mock skills
      const mockSkills = [
        {
          id: 'skill_1',
          name: 'JavaScript',
          category: 'technical',
          level: 'Advanced',
          proficiency: 85,
          yearsOfExperience: 3,
          lastAssessed: new Date().toISOString(),
          isVerified: false,
          endorsements: 0,
          projects: []
        },
        {
          id: 'skill_2',
          name: 'React Native',
          category: 'technical',
          level: 'Intermediate',
          proficiency: 70,
          yearsOfExperience: 2,
          lastAssessed: new Date().toISOString(),
          isVerified: false,
          endorsements: 0,
          projects: []
        },
        {
          id: 'skill_3',
          name: 'Communication',
          category: 'soft',
          level: 'Advanced',
          proficiency: 90,
          yearsOfExperience: 5,
          lastAssessed: new Date().toISOString(),
          isVerified: false,
          endorsements: 0,
          projects: []
        }
      ];
      
      this.skillsData.userSkills = mockSkills;
      await this.saveUserSkills();
      
      return {
        success: true,
        data: mockSkills
      };
      
    } catch (error) {
      console.error('❌ Error getting user skills:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add new skill
  async addSkill(userId, skillData) {
    try {
      console.log('➕ Adding new skill for:', userId);
      
      const newSkill = {
        id: `skill_${Date.now()}`,
        ...skillData,
        userId,
        dateAdded: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        isVerified: false,
        endorsements: 0,
        projects: [],
        proficiency: this.calculateInitialProficiency(skillData.level)
      };
      
      this.skillsData.userSkills.push(newSkill);
      await this.saveUserSkills();
      
      // Notify subscribers
      this.notifySubscribers('skill_added', newSkill);
      
      return {
        success: true,
        data: newSkill
      };
      
    } catch (error) {
      console.error('❌ Error adding skill:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update skill
  async updateSkill(userId, skillId, updates) {
    try {
      console.log('📝 Updating skill:', skillId);
      
      const skillIndex = this.skillsData.userSkills.findIndex(skill => skill.id === skillId);
      
      if (skillIndex === -1) {
        throw new Error('Skill not found');
      }
      
      this.skillsData.userSkills[skillIndex] = {
        ...this.skillsData.userSkills[skillIndex],
        ...updates,
        lastUpdated: new Date().toISOString()
      };
      
      await this.saveUserSkills();
      
      // Notify subscribers
      this.notifySubscribers('skill_updated', this.skillsData.userSkills[skillIndex]);
      
      return {
        success: true,
        data: this.skillsData.userSkills[skillIndex]
      };
      
    } catch (error) {
      console.error('❌ Error updating skill:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete skill
  async deleteSkill(userId, skillId) {
    try {
      console.log('🗑️ Deleting skill:', skillId);
      
      const skillIndex = this.skillsData.userSkills.findIndex(skill => skill.id === skillId);
      
      if (skillIndex === -1) {
        throw new Error('Skill not found');
      }
      
      const deletedSkill = this.skillsData.userSkills.splice(skillIndex, 1)[0];
      await this.saveUserSkills();
      
      // Notify subscribers
      this.notifySubscribers('skill_deleted', { skillId, skill: deletedSkill });
      
      return {
        success: true,
        data: { skillId }
      };
      
    } catch (error) {
      console.error('❌ Error deleting skill:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get skill by ID
  async getSkillById(skillId) {
    try {
      const skill = this.skillsData.userSkills.find(skill => skill.id === skillId);
      
      if (!skill) {
        throw new Error('Skill not found');
      }
      
      return {
        success: true,
        data: skill
      };
      
    } catch (error) {
      console.error('❌ Error getting skill by ID:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Start skill assessment
  async startSkillAssessment(userId, skillId) {
    try {
      console.log('📊 Starting skill assessment for:', skillId);
      
      const skill = this.skillsData.userSkills.find(s => s.id === skillId);
      if (!skill) {
        throw new Error('Skill not found');
      }
      
      // Generate assessment questions based on skill category
      const questions = this.generateAssessmentQuestions(skill);
      
      const assessment = {
        id: `assessment_${Date.now()}`,
        skillId,
        skillName: skill.name,
        userId,
        questions,
        startTime: new Date().toISOString(),
        status: 'in_progress',
        currentQuestion: 0,
        answers: {},
        timeLimit: 30 * 60 * 1000, // 30 minutes
        score: null
      };
      
      this.skillsData.skillAssessments.push(assessment);
      await this.saveSkillAssessments();
      
      return {
        success: true,
        data: assessment
      };
      
    } catch (error) {
      console.error('❌ Error starting skill assessment:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Submit assessment answer
  async submitAssessmentAnswer(assessmentId, questionIndex, answer) {
    try {
      console.log('✅ Submitting assessment answer for:', assessmentId);
      
      const assessment = this.skillsData.skillAssessments.find(a => a.id === assessmentId);
      if (!assessment) {
        throw new Error('Assessment not found');
      }
      
      assessment.answers[questionIndex] = {
        answer,
        timestamp: new Date().toISOString()
      };
      
      assessment.currentQuestion = questionIndex + 1;
      
      await this.saveSkillAssessments();
      
      return {
        success: true,
        data: assessment
      };
      
    } catch (error) {
      console.error('❌ Error submitting assessment answer:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Complete skill assessment
  async completeSkillAssessment(assessmentId) {
    try {
      console.log('🏁 Completing skill assessment:', assessmentId);
      
      const assessment = this.skillsData.skillAssessments.find(a => a.id === assessmentId);
      if (!assessment) {
        throw new Error('Assessment not found');
      }
      
      // Calculate score
      const score = this.calculateAssessmentScore(assessment);
      
      assessment.status = 'completed';
      assessment.endTime = new Date().toISOString();
      assessment.score = score;
      
      // Update skill proficiency based on assessment
      await this.updateSkillProficiency(assessment.skillId, score);
      
      await this.saveSkillAssessments();
      
      // Notify subscribers
      this.notifySubscribers('assessment_completed', assessment);
      
      return {
        success: true,
        data: assessment
      };
      
    } catch (error) {
      console.error('❌ Error completing skill assessment:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Get skill progress
  async getSkillProgress(userId, skillId) {
    try {
      console.log('📈 Getting skill progress for:', skillId);
      
      const progressKey = `${userId}_${skillId}`;
      let progress = this.skillsData.skillProgress[progressKey];
      
      if (!progress) {
        // Generate mock progress data
        progress = this.generateMockProgress(skillId);
        this.skillsData.skillProgress[progressKey] = progress;
        await this.saveSkillProgress();
      }
      
      return {
        success: true,
        data: progress
      };
      
    } catch (error) {
      console.error('❌ Error getting skill progress:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update skill proficiency
  async updateSkillProficiency(skillId, newScore) {
    try {
      const skillIndex = this.skillsData.userSkills.findIndex(skill => skill.id === skillId);
      
      if (skillIndex !== -1) {
        this.skillsData.userSkills[skillIndex].proficiency = newScore;
        this.skillsData.userSkills[skillIndex].lastAssessed = new Date().toISOString();
        this.skillsData.userSkills[skillIndex].level = this.calculateSkillLevel(newScore);
        
        await this.saveUserSkills();
        
        // Notify subscribers
        this.notifySubscribers('skill_proficiency_updated', {
          skillId,
          proficiency: newScore,
          level: this.skillsData.userSkills[skillIndex].level
        });
      }
      
    } catch (error) {
      console.error('❌ Error updating skill proficiency:', error);
    }
  }

  // Load skill recommendations
  async loadSkillRecommendations() {
    try {
      const recommendations = [
        {
          id: 'rec_1',
          skillName: 'TypeScript',
          category: 'technical',
          reason: 'Complements your JavaScript skills',
          priority: 'high',
          estimatedLearningTime: '2-3 months',
          resources: []
        },
        {
          id: 'rec_2',
          skillName: 'Project Management',
          category: 'business',
          reason: 'Essential for career advancement',
          priority: 'medium',
          estimatedLearningTime: '1-2 months',
          resources: []
        }
      ];
      
      this.skillsData.skillRecommendations = recommendations;
      await AsyncStorage.setItem('skill_recommendations', JSON.stringify(recommendations));
      
    } catch (error) {
      console.error('❌ Error loading skill recommendations:', error);
    }
  }

  // Generate assessment questions
  generateAssessmentQuestions(skill) {
    const baseQuestions = [
      {
        id: 'q1',
        question: `How would you rate your overall experience with ${skill.name}?`,
        type: 'multiple_choice',
        options: ['Beginner', 'Intermediate', 'Advanced', 'Expert'],
        correctAnswer: null // Self-assessment
      },
      {
        id: 'q2',
        question: `Which best describes your proficiency in ${skill.name}?`,
        type: 'multiple_choice',
        options: [
          'I can perform basic tasks with guidance',
          'I can work independently on most tasks',
          'I can handle complex tasks and mentor others',
          'I am considered an expert and can lead projects'
        ],
        correctAnswer: null
      }
    ];
    
    // Add category-specific questions
    if (skill.category === 'technical') {
      baseQuestions.push({
        id: 'q3',
        question: `How often do you use ${skill.name} in your work?`,
        type: 'multiple_choice',
        options: ['Daily', 'Weekly', 'Monthly', 'Rarely'],
        correctAnswer: null
      });
    }
    
    return baseQuestions;
  }

  // Calculate assessment score
  calculateAssessmentScore(assessment) {
    const answers = Object.values(assessment.answers);
    if (answers.length === 0) return 0;
    
    // Simple scoring based on self-assessment
    let totalScore = 0;
    answers.forEach(answer => {
      if (typeof answer.answer === 'string') {
        switch (answer.answer.toLowerCase()) {
          case 'expert':
          case 'daily':
            totalScore += 100;
            break;
          case 'advanced':
          case 'weekly':
            totalScore += 80;
            break;
          case 'intermediate':
          case 'monthly':
            totalScore += 60;
            break;
          case 'beginner':
          case 'rarely':
            totalScore += 40;
            break;
          default:
            totalScore += 50;
        }
      }
    });
    
    return Math.round(totalScore / answers.length);
  }

  // Calculate initial proficiency
  calculateInitialProficiency(level) {
    switch (level?.toLowerCase()) {
      case 'expert': return 95;
      case 'advanced': return 80;
      case 'intermediate': return 60;
      case 'beginner': return 30;
      default: return 50;
    }
  }

  // Calculate skill level from proficiency
  calculateSkillLevel(proficiency) {
    if (proficiency >= 90) return 'Expert';
    if (proficiency >= 75) return 'Advanced';
    if (proficiency >= 50) return 'Intermediate';
    return 'Beginner';
  }

  // Generate mock progress data
  generateMockProgress(skillId) {
    const dates = [];
    const scores = [];
    const now = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setMonth(date.getMonth() - i);
      dates.push(date.toISOString().split('T')[0]);
      
      // Generate realistic progress curve
      const baseScore = 40 + (6 - i) * 8;
      const variation = Math.random() * 10 - 5;
      scores.push(Math.max(0, Math.min(100, baseScore + variation)));
    }
    
    return {
      skillId,
      labels: dates,
      data: scores,
      latestScore: scores[scores.length - 1],
      improvement: scores[scores.length - 1] - scores[0],
      lastUpdated: new Date().toISOString()
    };
  }

  // Subscribe to skill events
  subscribe(event, callback) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, []);
    }
    this.subscribers.get(event).push(callback);
  }

  // Unsubscribe from skill events
  unsubscribe(event, callback) {
    if (this.subscribers.has(event)) {
      const callbacks = this.subscribers.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Notify subscribers
  notifySubscribers(event, data) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in subscriber callback for ${event}:`, error);
        }
      });
    }
  }

  // Save user skills
  async saveUserSkills() {
    try {
      await AsyncStorage.setItem('user_skills', JSON.stringify(this.skillsData.userSkills));
    } catch (error) {
      console.error('❌ Error saving user skills:', error);
    }
  }

  // Save skill assessments
  async saveSkillAssessments() {
    try {
      await AsyncStorage.setItem('skill_assessments', JSON.stringify(this.skillsData.skillAssessments));
    } catch (error) {
      console.error('❌ Error saving skill assessments:', error);
    }
  }

  // Save skill progress
  async saveSkillProgress() {
    try {
      await AsyncStorage.setItem('skill_progress', JSON.stringify(this.skillsData.skillProgress));
    } catch (error) {
      console.error('❌ Error saving skill progress:', error);
    }
  }

  // Get skill categories
  getSkillCategories() {
    return this.skillsData.skillCategories;
  }

  // Get skill recommendations
  getSkillRecommendations() {
    return this.skillsData.skillRecommendations;
  }

  // Search skills
  searchSkills(query) {
    const lowercaseQuery = query.toLowerCase();
    return this.skillsData.userSkills.filter(skill =>
      skill.name.toLowerCase().includes(lowercaseQuery) ||
      skill.category.toLowerCase().includes(lowercaseQuery)
    );
  }

  // Get skills by category
  getSkillsByCategory(category) {
    return this.skillsData.userSkills.filter(skill => skill.category === category);
  }

  // Get skill statistics
  getSkillStatistics() {
    const skills = this.skillsData.userSkills;
    const totalSkills = skills.length;
    const averageProficiency = totalSkills > 0 
      ? Math.round(skills.reduce((sum, skill) => sum + skill.proficiency, 0) / totalSkills)
      : 0;
    
    const categoryStats = {};
    skills.forEach(skill => {
      if (!categoryStats[skill.category]) {
        categoryStats[skill.category] = { count: 0, totalProficiency: 0 };
      }
      categoryStats[skill.category].count++;
      categoryStats[skill.category].totalProficiency += skill.proficiency;
    });
    
    Object.keys(categoryStats).forEach(category => {
      categoryStats[category].averageProficiency = Math.round(
        categoryStats[category].totalProficiency / categoryStats[category].count
      );
    });
    
    return {
      totalSkills,
      averageProficiency,
      categoryStats,
      verifiedSkills: skills.filter(skill => skill.isVerified).length,
      recentlyUpdated: skills.filter(skill => {
        const lastUpdate = new Date(skill.lastUpdated);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return lastUpdate > thirtyDaysAgo;
      }).length
    };
  }

  // Cleanup
  cleanup() {
    this.subscribers.clear();
    this.offlineQueue = [];
    this.isInitialized = false;
  }
}

export default SkillManagementService;

